<script setup>
import { ref } from 'vue'
import { formatMMMDYYYY, requirementGroupKey } from './helpers'

const props = defineProps({
  requirements: { type: Array, required: true },
  sortAsc: { type: Boolean, required: true },
  selectedKeys: { type: Object, required: true }, // Set
  isSelectAllChecked: { type: Boolean, required: true },
  historicalReminders: { type: Array, default: () => [] },
  flatKeys: { type: Array, default: () => [] }
})

const emits = defineEmits([
  'toggle-sort',
  'toggle-select-all',
  'toggle-row',
  'event-clicked'
])

const expandedKeys = ref(new Set())

function toggleExpand(key) {
  const newSet = new Set(expandedKeys.value)
  if (newSet.has(key)) {
    newSet.delete(key)
  } else {
    newSet.add(key)
  }
  expandedKeys.value = newSet
}

function isExpanded(key) {
  return expandedKeys.value.has(key)
}

function getVisibleEmployers(req) {
  if (isExpanded(req.key)) {
    return req.employers
  }
  return req.employers.slice(0, 3)
}

function onSelectAllChange(e) {
  emits('toggle-select-all', e.target.checked)
}

function onRowCheckboxChange(key, event) {
  const index = props.flatKeys.indexOf(key)
  emits('toggle-row', { key, index, shiftKey: event.shiftKey })
}

function onRequirementClicked(req) {
  const firstEmp = req.employers && req.employers.length > 0 ? req.employers[0] : null
  const eventPayload = {
    id: firstEmp?.eventId || null,
    name: req.requirementName,
    body: req.eventBody,
    date: req.eventMonthDay,
    dueDate: req.earliestDueDateObj
  }
  if (firstEmp) {
    emits('event-clicked', {
      employer: {
        employerName: firstEmp.employerName,
        plan_start_date: firstEmp.plan_start_date,
        employerId: firstEmp.employerId
      },
      event: eventPayload
    })
  }
}

/**
 * Get formatted sent dates for a specific requirement and employer.
 * Returns a string like "June 20, 2025" or "June 20, 2025 and June 21, 2025"
 */
function getSentDatesForEmployer(req, emp) {
  if (!props.historicalReminders || props.historicalReminders.length === 0) return null
  
  // Build the requirement key for this requirement
  const reqKey = requirementGroupKey(req.eventBody, req.eventMonthDay)
  
  // Find all reminders matching this employer + requirement
  const matchingReminders = props.historicalReminders.filter(r => 
    r.employer_id === emp.employerId && r.requirement_key === reqKey
  )
  
  if (matchingReminders.length === 0) return null
  
  // Extract and format dates, sort by date ascending
  const dates = matchingReminders
    .map(r => r.created_at ? new Date(r.created_at) : null)
    .filter(d => d && !isNaN(d))
    .sort((a, b) => a - b)
    .map(d => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }))
  
  const uniqueDates = [...new Set(dates)]
  
  if (uniqueDates.length === 0) return null
  if (uniqueDates.length === 1) return uniqueDates[0]
  if (uniqueDates.length === 2) return `${uniqueDates[0]} and ${uniqueDates[1]}`
  
  const allButLast = uniqueDates.slice(0, -1).join(', ')
  return `${allButLast} and ${uniqueDates[uniqueDates.length - 1]}`
}
</script>

<template>
  <table class="table table-sm align-middle mb-2" id="requirementDeadlinesTableRequirements">
    <thead>
      <tr>
        <th class="py-2.5">
          <button id="requirementSortToggle" class="btn btn-link p-0 text-decoration-none text-16 px-3.5" @click="$emit('toggle-sort')" title="Toggle sort">
            <strong class="text-secondary">Requirement</strong>
            <i class="bi ms-1" :class="sortAsc ? 'bi-sort-alpha-down' : 'bi-sort-alpha-up'" />
          </button>
        </th>
        <th style="width: 14%" class="py-2.5 text-16"><strong class="text-secondary px-3.5">Due Date</strong></th>
        <th style="width: 33%" class="py-2.5 text-16"><strong class="text-secondary px-3.5">Employer</strong></th>
        <th style="width: 18%" class="py-2.5 ps-4">
          <div class="form-check d-inline-flex align-items-start gap-2">
            <input class="form-check-input" type="checkbox" id="reqSelectAll" :checked="isSelectAllChecked" @change="onSelectAllChange">
            <label class="form-check-label" for="reqSelectAll">Select All</label>
          </div>
        </th>
      </tr>
    </thead>
    <tbody id="requirementDeadlinesTableBodyRequirements">
      <template v-if="requirements.length > 0">
        <tr v-for="req in requirements" :key="req.key" :id="`deadlineTableRow-${req.key}`">
          <td class="align-text-top py-2.5 px-4">
            <a href="#" class="text-primary text-decoration-none text-16" @click.prevent="onRequirementClicked(req)">{{ req.requirementName }}</a>
          </td>
            <td class="text-nowrap align-text-top py-2.5 px-4 text-16">{{ formatMMMDYYYY(req.earliestDueDateObj) }}</td>
          <td class="align-text-top py-2.5 px-4">
            <div class="text-16">
              <div v-for="(emp, index) in getVisibleEmployers(req)" :key="emp.employerId + '-' + index" class="mb-1">
                <div>{{ emp.employerName }}</div>
                <div>
                  <span
                    v-if="getSentDatesForEmployer(req, emp)"
                    class="reminder-sent-badge text-12 mt-1"
                  >
                    Reminder Sent {{ getSentDatesForEmployer(req, emp) }}
                  </span>
                </div>
              </div>
            </div>
            <div v-if="req.employers.length > 3">
              <button 
                class="btn btn-link text-decoration-none p-0 mt-1 d-flex align-items-center gap-2 text-16" 
                @click="toggleExpand(req.key)"
              >
                <med>
                  <span v-if="!isExpanded(req.key)">Show All {{ req.employers.length }} Employers</span>
                  <span v-else>Show Less</span>
                </med>
                <i class="bi" :class="isExpanded(req.key) ? 'bi-caret-up-fill' : 'bi-caret-down-fill'"></i>
              </button>
            </div>
          </td>
          <td class="align-text-top ps-4 py-2.5">
            <div class="form-check d-inline-flex gap-2">
              <input
                class="form-check-input"
                type="checkbox"
                :id="'req-'+req.key"
                :checked="selectedKeys.has(req.key)"
                @click="onRowCheckboxChange(req.key, $event)"
              >
              <label class="form-check-label" :for="'req-'+req.key">Send Reminder</label>
            </div>
          </td>
        </tr>
      </template>
      <tr v-else>
        <td colspan="4" class="text-center py-4 text-gray-600">No results</td>
      </tr>
    </tbody>
  </table>
</template>

<style scoped>
.table td, .table th { border: 1px solid #C5CDD4; }
.table tr:first-child th { border-top: none; }

.reminder-sent-badge {
  display: inline-block;
  background-color: var(--green-060);
  color: var(--green-500);
  padding: 4px 8px;
  border-radius: 4px;
}
</style>